// draftService.ts - Draft Management Service

import { FormData, Gate, Personnel } from './types/GatePassTypes';
import { API_BASE_URL } from './constants/GatePassConstants';

// ============================================
// INTERFACES
// ============================================

export interface SavedDraft {
  draftId: number;
  draftName: string;
  formData: FormData;
  gateIds: number[];
  personnelIds: number[];
  selectedGates?: Gate[];
  selectedPersonnel?: Personnel[];
  gates?: Gate[]; // For backward compatibility
  personnel?: Personnel[]; // For backward compatibility
  createdAt: string;
  updatedAt: string;
  userId: string;
}

export interface SaveDraftRequest {
  draftId?: number | null;
  draftName: string;
  formData: FormData;
  gateIds: number[];
  personnelIds: number[];
}

export interface SaveDraftResponse {
  success: boolean;
  message: string;
  data: {
    draftId: number;
    draftName: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface LoadDraftResponse {
  success: boolean;
  message: string;
  data: SavedDraft;
  error?: string; // Optional error field for failed responses
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Generate a suggested draft name based on form data
 */
export const generateDraftName = (formData: FormData): string => {
  const timestamp = new Date().toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  
  // Use company name or requester name as prefix
  const prefix = formData.companyName || formData.name || 'Gate Pass';
  
  // Add request type if specified
  const type = formData.requestType ? ` (${formData.requestType})` : '';
  
  return `${prefix}${type} - ${timestamp}`;
};

/**
 * Validate draft data before saving
 */
const validateDraftData = (draftData: SaveDraftRequest): { isValid: boolean; error?: string } => {
  if (!draftData.draftName?.trim()) {
    return { isValid: false, error: 'Draft name is required' };
  }
  
  if (draftData.draftName.length > 100) {
    return { isValid: false, error: 'Draft name must be less than 100 characters' };
  }
  
  if (!draftData.formData) {
    return { isValid: false, error: 'Form data is required' };
  }
  
  return { isValid: true };
};

/**
 * Create API request headers
 */
const createHeaders = async (getAccessToken: () => Promise<string>, includeContentType: boolean = true): Promise<HeadersInit> => {
  const accessToken = await getAccessToken();
  const headers: HeadersInit = {
    'Authorization': `Bearer ${accessToken}`
  };
  
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  
  return headers;
};

// ============================================
// DRAFT OPERATIONS
// ============================================

/**
 * Save a draft to the server
 */
export const saveDraft = async (
  draftData: SaveDraftRequest,
  getAccessToken: () => Promise<string>
): Promise<SaveDraftResponse> => {
  try {
    // Validate draft data
    const validation = validateDraftData(draftData);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }

    // Prepare the request - saveDraft handles both create and update via draftId in body
    const headers = await createHeaders(getAccessToken);
    const url = `${API_BASE_URL}/saveDraft`;
    
    const requestBody = {
      draftId: draftData.draftId || null,
      draftName: draftData.draftName,
      formData: draftData.formData,
      gateIds: draftData.gateIds,
      personnelIds: draftData.personnelIds
    };

    console.log(`${draftData.draftId ? 'Updating' : 'Creating'} draft:`, draftData.draftName);

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody)
    });

    const responseText = await response.text();
    let responseData;

    try {
      responseData = JSON.parse(responseText);
    } catch (parseError) {
      throw new Error(`Server returned invalid response. Status: ${response.status}`);
    }

    if (!response.ok) {
      const errorMessage = responseData.error || responseData.message || `HTTP ${response.status}`;
      throw new Error(errorMessage);
    }

    if (!responseData.success) {
      throw new Error(responseData.message || 'Failed to save draft');
    }

    console.log('Draft saved successfully:', responseData.data);
    return responseData;

  } catch (error) {
    console.error('Error saving draft:', error);
    
    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Unable to connect to server. Please check your internet connection.');
    }
    
    // Handle other errors
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred while saving draft';
    throw new Error(errorMessage);
  }
};

/**
 * Load a draft from the server
 */
export const loadDraft = async (
  draftId: number,
  getAccessToken: () => Promise<string>
): Promise<SavedDraft> => {
  try {
    if (!draftId || draftId <= 0) {
      throw new Error('Invalid draft ID provided');
    }


    const headers = await createHeaders(getAccessToken);
    const url = `${API_BASE_URL}/loadDraft/${draftId}`;

    console.log('Loading draft:', draftId);

    const response = await fetch(url, {
      method: 'GET',
      headers
    });

    const responseText = await response.text();
    let responseData: LoadDraftResponse;

    try {
      responseData = JSON.parse(responseText);
    } catch (parseError) {
      throw new Error(`Server returned invalid response. Status: ${response.status}`);
    }

    if (!response.ok) {
      const errorMessage = responseData.error || responseData.message || `HTTP ${response.status}`;
      if (response.status === 404) {
        throw new Error('Draft not found. It may have been deleted.');
      }
      throw new Error(errorMessage);
    }

    if (!responseData.success || !responseData.data) {
      throw new Error(responseData.message || 'Failed to load draft');
    }

    console.log('Draft loaded successfully:', responseData.data.draftName);
    return responseData.data;

  } catch (error) {
    console.error('Error loading draft:', error);
    
    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Unable to connect to server. Please check your internet connection.');
    }
    
    // Handle other errors
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred while loading draft';
    throw new Error(errorMessage);
  }
};

/**
 * Delete a draft from the server
 */
export const deleteDraft = async (
  draftId: number,
  getAccessToken: () => Promise<string>
): Promise<{ success: boolean; message: string }> => {
  try {
    if (!draftId || draftId <= 0) {
      throw new Error('Invalid draft ID provided');
    }

    const headers = await createHeaders(getAccessToken);
    const url = `${API_BASE_URL}/delete_drafts/${draftId}`;

    console.log('Deleting draft:', draftId);

    const response = await fetch(url, {
      method: 'DELETE',
      headers
    });

    const responseText = await response.text();
    let responseData;

    try {
      responseData = JSON.parse(responseText);
    } catch (parseError) {
      // If no JSON response, but successful status, assume success
      if (response.ok) {
        return { success: true, message: 'Draft deleted successfully' };
      }
      throw new Error(`Server returned invalid response. Status: ${response.status}`);
    }

    if (!response.ok) {
      const errorMessage = responseData.error || responseData.message || `HTTP ${response.status}`;
      if (response.status === 404) {
        throw new Error('Draft not found. It may have already been deleted.');
      }
      throw new Error(errorMessage);
    }

    console.log('Draft deleted successfully');
    return { success: true, message: 'Draft deleted successfully' };

  } catch (error) {
    console.error('Error deleting draft:', error);
    
    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Unable to connect to server. Please check your internet connection.');
    }
    
    // Handle other errors
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred while deleting draft';
    throw new Error(errorMessage);
  }
};

/**
 * Get all drafts for the current user
 */
export const getUserDrafts = async (
  getAccessToken: () => Promise<string>
): Promise<SavedDraft[]> => {
  try {
    const headers = await createHeaders(getAccessToken);
    const url = `${API_BASE_URL}/listDrafts`;

    console.log('Loading user drafts');

    const response = await fetch(url, {
      method: 'GET',
      headers
    });

    const responseText = await response.text();
    let responseData;

    try {
      responseData = JSON.parse(responseText);
    } catch (parseError) {
      throw new Error(`Server returned invalid response. Status: ${response.status}`);
    }

    if (!response.ok) {
      const errorMessage = responseData.error || responseData.message || `HTTP ${response.status}`;
      throw new Error(errorMessage);
    }

    if (!responseData.success) {
      throw new Error(responseData.message || 'Failed to load drafts');
    }

    const drafts = responseData.data || [];
    console.log(`Loaded ${drafts.length} drafts`);
    return drafts;

  } catch (error) {
    console.error('Error loading user drafts:', error);
    
    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Unable to connect to server. Please check your internet connection.');
    }
    
    // Handle other errors
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred while loading drafts';
    throw new Error(errorMessage);
  }
};

/**
 * Create a new draft with minimal data
 */
export const createQuickDraft = async (
  formData: FormData,
  getAccessToken: () => Promise<string>
): Promise<SaveDraftResponse> => {
  const draftData: SaveDraftRequest = {
    draftName: generateDraftName(formData),
    formData,
    gateIds: [],
    personnelIds: []
  };

  return saveDraft(draftData, getAccessToken);
};

/**
 * Check if a draft name already exists
 */
export const isDraftNameExists = async (
  draftName: string,
  excludeDraftId: number | null,
  getAccessToken: () => Promise<string>
): Promise<boolean> => {
  try {
    const drafts = await getUserDrafts(getAccessToken);
    
    return drafts.some(draft => 
      draft.draftName.toLowerCase() === draftName.toLowerCase() && 
      draft.draftId !== excludeDraftId
    );
  } catch (error) {
    console.error('Error checking draft name:', error);
    // If we can't check, assume it doesn't exist
    return false;
  }
};

/**
 * Auto-save functionality with debouncing
 */
let autoSaveTimeout: NodeJS.Timeout | null = null;

export const scheduleAutoSave = (
  draftData: SaveDraftRequest,
  getAccessToken: () => Promise<string>,
  delay: number = 2000 // 2 seconds delay
): Promise<SaveDraftResponse> => {
  return new Promise((resolve, reject) => {
    // Clear existing timeout
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }

    // Schedule new auto-save
    autoSaveTimeout = setTimeout(async () => {
      try {
        const result = await saveDraft(draftData, getAccessToken);
        resolve(result);
      } catch (error) {
        reject(error);
      }
    }, delay);
  });
};

/**
 * Cancel scheduled auto-save
 */
export const cancelAutoSave = (): void => {
  if (autoSaveTimeout) {
    clearTimeout(autoSaveTimeout);
    autoSaveTimeout = null;
  }
};

// ============================================
// UTILITIES FOR FORM INTEGRATION
// ============================================

/**
 * Prepare form data for draft saving (remove sensitive info if needed)
 */
export const prepareDraftFormData = (formData: FormData): FormData => {
  // Create a copy and remove any sensitive information
  const safeCopy = { ...formData };
  
  // You might want to remove sensitive fields here
  // For example:
  // delete safeCopy.sensitiveField;
  
  return safeCopy;
};

/**
 * Merge loaded draft data with current form defaults
 */
export const mergeDraftWithDefaults = (
  loadedDraft: SavedDraft,
  defaultFormData: FormData
): FormData => {
  if (!loadedDraft.formData) {
    return defaultFormData;
  }

  // Merge loaded data with defaults, giving preference to loaded data
  return {
    ...defaultFormData,
    ...loadedDraft.formData,
    // Ensure critical fields are not null/undefined
    regionId: loadedDraft.formData.regionId ?? defaultFormData.regionId,
    requestType: loadedDraft.formData.requestType || defaultFormData.requestType,
    passType: loadedDraft.formData.passType || defaultFormData.passType
  };
};